/**
 * Common NavigationCard component used by other modules as a reusable component which returns the ui for a product rail horizontal or a product rail vertical.
 *
 * @module views/components/NavigationCard
 * @memberof -Common
 */
import './NavigationCard.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';


/**
 * Represents a NavigationCard component
 *
 * @method
 * @param {NavigationCardProps} props - React properties passed from composition
 * @returns nNavigationCard
 */
const NavigationCard = function( props ){
  const {
    action,
    altText,
    backgroundColor,
    image,
    imageUrl,
    imageMetaData,
    shopAllLabel,
    shopAllAction,
    outlineOption
  } = props;
  const formattedImageUrl = image ? image.imageUrl : imageUrl;
  const preferShopAll = shopAllAction && shopAllLabel && !formattedImageUrl;
  const preferredAction = preferShopAll ? shopAllAction : action;

  return (
    <Link_Huge
      target='_self'
      action={ preferredAction }
      className='NavigationCard'
    >
      <div className={ classNames( 'NavigationCard__container', {
        [`NavigationCard__container--${ backgroundColor }`]: backgroundColor,
        [`NavigationCard__container--TEXT`]: preferShopAll,
        [`NavigationCard__container--LOGO`]: outlineOption
      } ) }
      >
        { formattedImageUrl &&
          <Image
            alt={ altText }
            src={ formattedImageUrl }
            metaData={ {
              imageTemplate: 'ProductCardNeutralBG'
            } }
            { ...( imageMetaData && { metaData: imageMetaData } ) }
          />
        }
        { preferShopAll &&
          <Text textAlign='center'
            textStyle='body-2'
          >
            { shopAllLabel }
          </Text>
        }
      </div>
      { preferredAction?.label && (
        <div className='NavigationCard__label'>
          <Text textAlign='center'
            textStyle='body-3'
          >
            { preferredAction?.label }
          </Text>
        </div>
      ) }
    </Link_Huge>
  );
};

/**
 * Property type definitions
 * @typedef NavigationCardProps
 * @type {object}
 * @property {string} action - Sets the action object for the card.
 * @property {string} backgroundColor - Sets background color for the card.
 * @property {string} image - Sets the image or illustration for the card.
 * @property {string} imageUrl - Sets the image or illustration for the card.
 * @property {string} imageMetaData - Sets the image metadata for the card.
 * @property {object} label - Sets the text, if applicable, in lieu of an image for the card.
 * @property {object} outlineOption - True if we want a border; otherwise, false
 * @property {object} shopAllLabel - Sets the Shop All text for the card.
 * @property {object} shopAllAction - Sets the Shop All action for the card.
 */
export const propTypes = {
  action: PropTypes.object,
  backgroundColor: PropTypes.string,
  image: PropTypes.object,
  imageUrl: PropTypes.string,
  imageMetaData: PropTypes.object,
  label: PropTypes.string,
  outlineOption: PropTypes.bool,
  shopAllLabel: PropTypes.string,
  shopAllAction: PropTypes.object
};

NavigationCard.propTypes = propTypes;

export default NavigationCard;
